import React, { useState, useEffect, useCallback } from 'react';
import logger from '../lib/logger';
import { 
  BarChart3, 
  Eye, 
  ThumbsUp, 
  MessageCircle, 
  Share2,
  Clock,
  TrendingUp,
  RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { AgentCollectionBanner } from '@/components/AgentCollectionBanner';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import * as api from '@/lib/api';

const MetricCard = ({ title, value, icon: Icon, change, loading }) => (
  <Card className="bg-card border-border">
    <CardContent className="p-6">
      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-8 w-28" />
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-muted-foreground">{title}</p>
            <Icon className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
          </div>
          <p className="text-3xl font-heading font-bold">
            {typeof value === 'number' ? value.toLocaleString() : value}
          </p>
          {change !== undefined && (
            <div className="flex items-center gap-1 mt-2">
              <TrendingUp className={`w-4 h-4 ${change >= 0 ? 'text-green-500' : 'text-red-500'}`} />
              <span className={`text-sm ${change >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                {change >= 0 ? '+' : ''}{change}%
              </span>
            </div>
          )}
        </>
      )}
    </CardContent>
  </Card>
);

const PLATFORM_COLORS = {
  youtube: '#FF0000',
  tiktok: '#00F2EA',
  instagram: '#E1306C'
};

// Custom Tooltip component for charts
const ChartTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-card border border-border rounded-lg p-3 shadow-xl">
        <p className="text-sm font-medium mb-2">{label}</p>
        {payload.map((entry, index) => (
          <p key={`${entry.name}-${index}`} className="text-xs" style={{ color: entry.color }}>
            {entry.name}: {entry.value.toLocaleString()}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export default function Analytics() {
  const [overview, setOverview] = useState(null);
  const [trends, setTrends] = useState([]);
  const [topClips, setTopClips] = useState([]);
  const [platformData, setPlatformData] = useState([]);
  const [collection, setCollection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [overviewRes, trendsRes, topRes, statusRes] = await Promise.all([
        api.getAnalyticsOverview(),
        api.getAnalyticsTrends(parseInt(timeRange)),
        api.getTopClips(10, 'views'),
        api.getCollectionStatus()
      ]);

      setOverview(overviewRes.data);
      setCollection(statusRes.data);
      setTrends(trendsRes.data.trends || []);
      setTopClips(topRes.data.clips || []);

      // Fetch platform data
      const platforms = ['youtube', 'tiktok', 'instagram'];
      const platformResults = await Promise.all(
        platforms.map(p => api.getPlatformAnalytics(p))
      );
      setPlatformData(platformResults.map(r => r.data));

    } catch (error) {
      logger.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics');
      setTrends([]);
      setTopClips([]);
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const pieData = platformData.map((p, i) => ({
    name: ['YouTube', 'TikTok', 'Instagram'][i],
    value: p.total_views || 0,
    color: Object.values(PLATFORM_COLORS)[i]
  }));

  return (
    <div className="space-y-6 max-w-7xl mx-auto" data-testid="analytics-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold">Analytics</h1>
          <p className="text-muted-foreground">Track your clip performance across platforms</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-36 bg-muted/50" data-testid="time-range-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={fetchData} variant="outline" data-testid="refresh-analytics-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      <AgentCollectionBanner status={collection} onRequested={fetchData} />

      {/* Overview Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Views"
          value={overview?.total_views || 0}
          icon={Eye}
          loading={loading}
        />
        <MetricCard
          title="Total Likes"
          value={overview?.total_likes || 0}
          icon={ThumbsUp}
          loading={loading}
        />
        <MetricCard
          title="Comments"
          value={overview?.total_comments || 0}
          icon={MessageCircle}
          loading={loading}
        />
        <MetricCard
          title="Engagement Rate"
          value={`${overview?.engagement_rate || 0}%`}
          icon={TrendingUp}
          loading={loading}
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Trends Chart */}
        <Card className="lg:col-span-2 bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-heading">Performance Trends</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-[300px] w-full" />
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={trends}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis 
                    dataKey="date" 
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                    tickFormatter={(value) => value.slice(5)}
                  />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <Tooltip content={<ChartTooltip />} />
                  <Line
                    type="monotone"
                    dataKey="views"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    dot={false}
                    name="Views"
                  />
                  <Line
                    type="monotone"
                    dataKey="likes"
                    stroke="hsl(var(--secondary))"
                    strokeWidth={2}
                    dot={false}
                    name="Likes"
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Platform Distribution */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-heading">Platform Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-[250px] w-full" />
            ) : (
              <>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex justify-center gap-4 mt-2">
                  {pieData.map((item) => (
                    <div key={item.name} className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                      <span className="text-xs text-muted-foreground">{item.name}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Platform Breakdown & Top Clips */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Platform Stats */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-heading">Platform Performance</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map(i => <Skeleton key={`skeleton-platform-${i}`} className="h-16 w-full" />)}
              </div>
            ) : (
              <div className="space-y-4">
                {platformData.map((platform, index) => {
                  const names = ['YouTube', 'TikTok', 'Instagram'];
                  const colors = Object.values(PLATFORM_COLORS);
                  
                  return (
                    <div 
                      key={names[index]}
                      className="p-4 rounded-lg bg-muted/30 border border-border"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: colors[index] }}
                          />
                          <span className="font-medium">{names[index]}</span>
                        </div>
                        <Badge variant="outline">
                          {((platform.avg_retention || 0)).toFixed(1)}% retention
                        </Badge>
                      </div>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground text-xs">Views</p>
                          <p className="font-medium">{(platform.total_views || 0).toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground text-xs">Likes</p>
                          <p className="font-medium">{(platform.total_likes || 0).toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground text-xs">Comments</p>
                          <p className="font-medium">{(platform.total_comments || 0).toLocaleString()}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Clips */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-heading">Top Performing Clips</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map(i => <Skeleton key={`skeleton-clip-${i}`} className="h-14 w-full" />)}
              </div>
            ) : topClips.length > 0 ? (
              <div className="space-y-3">
                {topClips.slice(0, 5).map((clip, index) => (
                  <div 
                    key={clip.id}
                    className="flex items-center gap-3 p-3 rounded-lg bg-muted/30"
                  >
                    <span className="text-lg font-bold text-muted-foreground w-6">
                      #{index + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{clip.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {(clip.views || 0).toLocaleString()} views
                      </p>
                    </div>
                    <Badge variant="outline" className="text-green-500">
                      {(clip.retention_rate || 0).toFixed(0)}%
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-8 text-center">
                <BarChart3 className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                <p className="text-sm text-muted-foreground">No clip data yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
